import React, { createRef, useMemo, useCallback } from "react";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { isSpectator, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { CircleIcon, PrimaryButton, SquareIcon, TopBar } from "@kalyzee/kalyzee-ui";
import { LayoutType } from "../../interfaces/ui";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { toastError } from "../../helpers/toast";
import OverlayInformationMessage from "../utils/OverlayInformationMessage";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isExpert } from "../../helpers/roles";
import { HighlightButton } from "../highlight/button";
import { AppSoundSlider } from "../utils/AppSoundSlider";
import { MediaSourceViewer } from "./MediaSourceViewer";
import { MeetingSourceExtended } from "../../hooks/meeting";
import styles from "./BottomBar.module.css"

export const BottomBar = React.memo(({
  onPause,
  onStop,
  sources,
  showLayoutSelector,
  onOpenSources,
  notificationCount,
  sourcesActive,
  onRequestHighlight,
}: {
  onUpdateMeetingState: (state: MeetingState) => void;
  onPause: () => void;
  onStop: () => void;
  sources: MeetingSourceExtended[];
  showLayoutSelector?: boolean;
  onOpenSources?: () => void;
  notificationCount?: number;
  sourcesActive?: boolean;
  onRequestHighlight?: () => void;
}) => {
  const { containerAPI } = useMeetingContainerContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const [audioPermitted] = useMeetingSelector(containerAPI, "session", session => session?.permissions?.audio ?? true);
  const [meetingShortId] = useMeetingSelector(containerAPI, "meeting", meeting => meeting?.shortId);

  const layout = useAppStoreSelector(state => state.layout);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const popupMode = useAppStoreSelector(state => state.popupMode);

  const dispatch = useAppStoreDispatch();

  const hasBackendMedias = !!session?.medias?.length;

  const handleCameraToggle = useCallback(async () => {
    if (!containerAPI) return;
    const newEnabled = !videoEnabled;

    if (hasBackendMedias) {
      const manager = containerAPI.emissionManager;
      let success = true;
      if (manager.videoIsEnabled({ mode: "some" })) {
        success = (await containerAPI.disableVideo({ direction: "outgoing" })).success;
      } else {
        success = (await containerAPI.enableVideo({ direction: "outgoing" })).success;
      }
      if (!success) toastError("Une erreur est survenue...");
      dispatch("setVideoStreamEnabled", manager.videoIsEnabled({ mode: "some" }));
    } else {
      dispatch("setVideoStreamEnabled", newEnabled);
    }
  }, [containerAPI, dispatch, videoEnabled, hasBackendMedias]);

  const handleAudioToggle = useCallback(async () => {
    if (!containerAPI) return;
    const newEnabled = !audioEnabled;

    if (hasBackendMedias) {
      const manager = containerAPI.emissionManager;
      let success = true;
      if (manager.audioIsEnabled({ mode: "some" })) {
        success = (await containerAPI.mute({ direction: "outgoing" })).success;
      } else {
        success = (await containerAPI.unmute({ direction: "outgoing" })).success;
      }
      if (!success) toastError("Une erreur est survenue...");
      dispatch("setAudioStreamEnabled", manager.audioIsEnabled({ mode: "some" }));
    } else {
      dispatch("setAudioStreamEnabled", newEnabled);
    }
  }, [containerAPI, dispatch, audioEnabled, hasBackendMedias]);

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);

  const sourcesActive_ = popupMode === "sources";
  const shareActive = popupMode === "share";

  const handleSourcePickerToggle = useCallback(() => {
    dispatch("setPopupMode", sourcesActive_ ? undefined : "sources");
  }, [dispatch, sourcesActive_]);

  const handleShareClick = useCallback(() => {
    dispatch("setPopupMode", shareActive ? undefined : "share");
  }, [dispatch, shareActive]);

  const handleWorkBoxToggle = useCallback(() => {
    dispatch("setWorkBoxOpen", !workBoxOpen);
  }, [dispatch, workBoxOpen]);

  const left = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (isMobile) {
      // Mobile: workbox toggle button
      items.push(
        <PrimaryButton onClick={handleWorkBoxToggle} key="workbox-toggle">
          {CircleIcon.Variants.WorkBox({
            active: workBoxOpen,
            notificationCount: !workBoxOpen ? notificationCount : undefined,
          })}
        </PrimaryButton>
      );
    }

    if (showLayoutSelector) {
      const size = isMobile ? 36 : 50;
      const icon = isMobile ? 18 : 25;
      items.push(
        <div className={styles.layoutSelector} key="layout-selector">
          <SquareIcon.Variants.GridLayout
            containerSize={size}
            iconSize={icon}
            selected={layout === LayoutType.Square}
            onClick={() => dispatch("setLayout", LayoutType.Square)}
          />
          <SquareIcon.Variants.HorizontalLayout
            containerSize={size}
            iconSize={icon}
            selected={layout === LayoutType.HorizontalSquare}
            onClick={() => dispatch("setLayout", LayoutType.HorizontalSquare)}
          />
          {!isMobile && (
            <SquareIcon.Variants.VerticalLayout
              containerSize={size}
              iconSize={icon}
              selected={layout === LayoutType.VerticalSquare}
              onClick={() => dispatch("setLayout", LayoutType.VerticalSquare)}
            />
          )}
        </div>
      );
    }

    return items;
  }, [showLayoutSelector, layout, dispatch, isMobile, handleWorkBoxToggle, workBoxOpen, notificationCount, sourcesActive]);

  const renderCameraButton = useMemo(() => {
    const permitted = !(session?.permissions?.video === false);
    const currRef = createRef<HTMLDivElement>();
    let hoverMessage: string | undefined = undefined;
    if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
    return (
      <div ref={currRef} key="student-dashboard-overlay-camera-btn">
        <PrimaryButton onClick={handleCameraToggle}>
          <CircleIcon.Variants.Camera
            unavailable={!permitted}
            disabled={!videoEnabled}
          />
        </PrimaryButton>
        {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
      </div>
    );
  }, [session?.permissions?.video, videoEnabled, handleCameraToggle]);

  const renderAudioButton = useMemo(() => {
    const currRef = createRef<HTMLDivElement>();
    let hoverMessage: string | undefined = undefined;
    if (!audioPermitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
    return (
      <div ref={currRef} key="student-dashboard-overlay-microphone-btn">
        <PrimaryButton onClick={handleAudioToggle}>
          <CircleIcon.Variants.Mic
            unavailable={!audioPermitted}
            disabled={!audioEnabled}
          />
        </PrimaryButton>
        {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
      </div>
    );
  }, [audioPermitted, audioEnabled, handleAudioToggle]);
  
  const renderPauseButton = useMemo(() => (
    <PrimaryButton onClick={onPause} key="pause-btn">
      <CircleIcon.Variants.Pause />
    </PrimaryButton>
  ), [onPause]);

  const renderHangOffButton = useMemo(() => (
    <PrimaryButton onClick={onStop} key="hangoff-btn">
      <CircleIcon.Variants.HangOff
        style={{
          padding: "0.75em",
        }}
      />
    </PrimaryButton>
  ), [onStop]);

  const center = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (isExpert(session)) {
      items.push(renderPauseButton);
      items.push(renderHangOffButton);
    }

    // Show media buttons for non-spectators, or spectators who have backend medias (on the board)
    if (!isSpectator(session) || session?.medias?.length) {
      items.push(renderCameraButton);
      items.push(renderAudioButton);
    }

    // Show highlight button for non-experts (spectators included)
    if (!isExpert(session)) {
      items.push(<HighlightButton key="highlight-btn" onRequestHighlight={onRequestHighlight} />);
    }

    return items;
  }, [session, renderPauseButton, renderHangOffButton, renderCameraButton, renderAudioButton]);

  const right = useMemo(() => {
    if (isMobile) {
      const items: React.ReactNode[] = [];
      if (meetingShortId) {
        items.push(
          <SquareIcon.Variants.QRCode
            key="share-toggle"
            containerSize={36}
            iconSize={18}
            selected={shareActive}
            onClick={handleShareClick}
          />
        );
      }
      if (onOpenSources && sources.length) {
        items.push(
          <PrimaryButton onClick={onOpenSources} key="source-toggle">
            {CircleIcon.Variants.Sources({ active: sourcesActive })}
          </PrimaryButton>
        );
      }
      return items;
    }
    const items: React.ReactNode[] = [<AppSoundSlider key="sound-slider" />];
    if (meetingShortId) {
      items.push(
        <SquareIcon.Variants.QRCode
          key="share-toggle"
          containerSize={50}
          iconSize={25}
          selected={shareActive}
          onClick={handleShareClick}
        />
      );
    }
    items.push(
      <MediaSourceViewer
        key="media-source-viewer"
        sources={sources}
        onClick={handleSourcePickerToggle}
        selected={sourcesActive_}
      />
    );
    return items;
  }, [sources, handleSourcePickerToggle, sourcesActive_, isMobile, onOpenSources, sourcesActive, meetingShortId, handleShareClick, shareActive]);

  const style = useMemo(() => ({
    height: "unset",
    padding: "unset",
    backgroundColor: "transparent",
    boxShadow: "unset",
  }), []);

  if (!session) return null;
  
  return (
    <TopBar 
      style={style}
      left={left}
      center={center}
      right={right}
    />
  );
});